import Head from 'next/head';
import { FC } from 'react';
import { useSelector } from 'react-redux';

import type { RightPsychic } from 'types/objectTypes';
import type { Store } from 'app-redux/types/storeTypes';

export const buildPersonSchema = (
  psychic: RightPsychic,
  pageUrl: string,
): Record<string, any> => {
  const {
    lineName,
    psychicImageUrl,
    images,
    usp,
    bio,
    basePrice,
    overallScore,
    responses,
    skills,
    specialities,
    tools,
    style,
  } = psychic;

  const image = psychicImageUrl || images?.[0];
  const knowsAbout = [
    ...(skills ? skills.split(',').map((s) => s.trim()).filter(Boolean) : []),
    ...(specialities || []),
    ...(tools || []),
    ...(style ? [style] : []),
  ].filter(Boolean);

  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    jobTitle: 'Psychic Advisor',
    worksFor: {
      '@type': 'Organization',
      name: 'California Psychics',
      url: process.env.BASE_SERVER_URL || process.env.NEXT_PUBLIC_BASE_SERVER_URL,
    },
  };

  if (lineName) { schema.name = lineName; }

  if (pageUrl) { schema.url = pageUrl; }

  if (image) { schema.image = image; }

  if (bio || usp) { schema.description = bio ? `${lineName} ${bio}` : usp; }

  if (knowsAbout.length) { schema.knowsAbout = knowsAbout; }

  if (basePrice) { schema.priceRange = `${basePrice}/min`; }

  if (overallScore && responses) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: overallScore.toFixed(1),
      reviewCount: String(responses),
      bestRating: '5',
      worstRating: '1',
    };
  }

  return schema;
};

export const PsychicBioSchemaScript: FC = () => {
  const data = useSelector((store: Store) => store.server.page.data);
  const base = process.env.BASE_SERVER_URL || process.env.NEXT_PUBLIC_BASE_SERVER_URL;

  const psychic = data.psychic as RightPsychic | undefined;
  const { extId } = data;

  if (!psychic || !extId) {
    return <></>;
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: base,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Psychics',
        item: `${base}/psychic-readings`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: psychic.lineName,
        item: `${base}/psychics/${psychic.lineName?.toLowerCase()}-${extId}`,
      },
    ],
  };

  return (
    <Head>
      <script
        key="breadcrumb-schema"
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema)
            .replace(/</g, '\\u003c')
            .replace(/>/g, '\\u003e'),
        }}
      />
    </Head>
  );
};
